import React, { useState } from "react";
import Modal from "react-modal";
import { X, ChevronDown, Search } from "lucide-react";
import { adminServices } from "../services/AdminServices";

Modal.setAppElement('#root');

const getTodayDate = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const initialFormData = {
  orderId: "OS-2026-001",
  dateIssued: getTodayDate(),
  orderType: "Special Order",
  category: "Market",
  accountType: "Merchant Account",
  pdlKeyword: "",
  pdlNumber: "",
  name: "",
  location: "",
  reasonForPurchase: "",
  merchantId: 0,
  storeCode: "",
  walletBalance: 0
};

const NewOrderModal = ({ isOpen, onClose, onProceed }) => {
  const [formData, setFormData] = useState(initialFormData);

  const isSpecialOrder = formData.orderType === "Special Order";

  const [pdlSearchResults, setPdlSearchResults] = useState([]);
  const [pdlSearching, setPdlSearching] = useState(false);
  const [showPdlDropdown, setShowPdlDropdown] = useState(false);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSearchPdl = async () => {
    if (!formData.pdlKeyword.trim()) return;
    setPdlSearching(true);
    setPdlSearchResults([]);
    setFormData(prev => ({
      ...prev,
      pdlNumber: "",
      name: "",
      location: "",
      merchantId: 0,
      storeCode: "",
      walletBalance: 0
    }));
    try {
      // Use searchMerchants if account type is Merchant Account
      const isMerchantAccount = formData.accountType === "Merchant Account";
      const res = isMerchantAccount 
        ? await adminServices.searchMerchants(formData.pdlKeyword)
        : await adminServices.searchPdl(formData.pdlKeyword);
      const raw = Array.isArray(res?.data) ? res.data : (res?.data?.data ?? res?.data?.items ?? []);
      setPdlSearchResults(raw);
      setShowPdlDropdown(true);
    } catch (err) {
      console.error("Search failed:", err);
      setPdlSearchResults([]);
    } finally {
      setPdlSearching(false);
    }
  };

  const handleSelectPdl = (pdl) => {
    const isMerchantAccount = formData.accountType === "Merchant Account";
    
    if (isMerchantAccount) {
      // Handle merchant result structure
      setFormData(prev => ({
        ...prev,
        pdlNumber: "",
        name: pdl.merchantName ?? "",
        location: pdl.location ?? "",
        merchantId: pdl.merchantId ?? 0,
        storeCode: pdl.merchantId ? `MERCH-${pdl.merchantId}` : "",
        walletBalance: pdl.walletBalance ?? 0
      }));
    } else {
      // Handle PDL result structure
      const facility = pdl.facilityName ?? pdl.facility ?? "";
      const colony = pdl.colonyName ?? pdl.colony ?? "";
      const location = [facility, colony].filter(Boolean).join(", ") || (pdl.location ?? pdl.storeName ?? "");
      setFormData(prev => ({
        ...prev,
        pdlNumber: pdl.pdlNumber ?? pdl.pdl_number ?? "",
        name: pdl.pdlName ?? pdl.name ?? pdl.fullName ?? pdl.firstName ?? "",
        location,
        merchantId: pdl.clientId ?? pdl.merchantId ?? pdl.id ?? 0,
        storeCode: pdl.storeCode ?? pdl.store_code ?? "",
        walletBalance: pdl.walletBalance ?? 0
      }));
    }
    setShowPdlDropdown(false);
  };

  const handleProceed = () => {
    if (onProceed) {
      const orderType = formData.orderType === "Special Order" ? "Special" : "Regular";
      onProceed({
        ...formData,
        orderType
      });
    }
    // Clear fields after proceeding
    setFormData(initialFormData);
    setPdlSearchResults([]);
    setShowPdlDropdown(false);

    if (formData.orderType !== "Special Order") {
      onClose();
    }
  };

  const handleClose = () => {
    setFormData(initialFormData);
    setPdlSearchResults([]);
    setShowPdlDropdown(false);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleClose}
      style={{
        overlay: { backgroundColor: 'rgba(0, 0, 0, 0.75)', zIndex: 1000 },
        content: {
          position: 'relative',
          backgroundColor: '#ffffff',
          borderRadius: '12px',
          maxWidth: '600px',
          width: '90%',
          maxHeight: '90vh',
          margin: 'auto',
          padding: '0',
          outline: 'none',
          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.3)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
      shouldCloseOnOverlayClick={false}
      contentLabel="Create New Order"
    >
      <div className="new-order-modal-content new-order-modal-scrollable">
        <button className="new-order-modal-close" onClick={handleClose}>
          <X size={24} />
        </button>
        <h2 className="new-order-modal-title">Create New Order</h2>

        <div className="new-order-modal-form">
          <div className="new-order-modal-row">
            <div className="new-order-modal-field new-order-modal-field-half">
              <label className="new-order-modal-label">Order ID</label>
              <input
                type="text"
                className="new-order-modal-input"
                value={formData.orderId}
                onChange={(e) => handleChange('orderId', e.target.value)}
              />
            </div>
            <div className="new-order-modal-field new-order-modal-field-half">
              <label className="new-order-modal-label">Date Issued</label>
              <input
                type="date"
                className="new-order-modal-input"
                value={formData.dateIssued}
                onChange={(e) => handleChange('dateIssued', e.target.value)}
              />
            </div>
          </div>

          <div className="new-order-modal-row">
            <div className="new-order-modal-field new-order-modal-field-half">
              <label className="new-order-modal-label">Order Type</label>
              <div className="new-order-modal-select-wrapper">
                <select
                  className="new-order-modal-select"
                  value={formData.orderType}
                  onChange={(e) => handleChange('orderType', e.target.value)}
                >
                  <option value="Regular Order">Regular Order</option>
                  <option value="Special Order">Special Order</option>
                </select>
                <ChevronDown className="new-order-modal-select-icon" size={20} />
              </div>
            </div>
            {isSpecialOrder ? (
              <div className="new-order-modal-field new-order-modal-field-half">
                <label className="new-order-modal-label">Account Type</label>
                <div className="new-order-modal-select-wrapper">
                  <select
                    className="new-order-modal-select"
                    value={formData.accountType}
                    onChange={(e) => handleChange('accountType', e.target.value)}
                  >
                    <option value="Merchant Account">Merchant Account</option>
                    <option value="Regular PDL">Regular PDL</option>
                    <option value="Special Account">Special Account</option>
                  </select>
                  <ChevronDown className="new-order-modal-select-icon" size={20} />
                </div>
              </div>
            ) : (
              <div className="new-order-modal-field new-order-modal-field-half">
                <label className="new-order-modal-label">Category</label>
                <div className="new-order-modal-select-wrapper">
                  <select
                    className="new-order-modal-select"
                    value={formData.category}
                    onChange={(e) => handleChange('category', e.target.value)}
                  >
                    <option value="Market/Grocery">Market/Grocery</option>
                    <option value="Market">Market</option>
                    <option value="Grocery">Grocery</option>
                    <option value="Special">Special</option>
                  </select>
                  <ChevronDown className="new-order-modal-select-icon" size={20} />
                </div>
              </div>
            )}
          </div>
          {!isSpecialOrder && (
            <div className="new-order-modal-field">
              <label className="new-order-modal-label">Account Type</label>
              <div className="new-order-modal-select-wrapper">
                <select
                  className="new-order-modal-select"
                  value={formData.accountType}
                  onChange={(e) => handleChange('accountType', e.target.value)}
                >
                  <option value="Merchant Account">Merchant Account</option>
                  <option value="Regular PDL">Regular PDL</option>
                  <option value="Special Account">Special Account</option>
                </select>
                <ChevronDown className="new-order-modal-select-icon" size={20} />
              </div>
            </div>
          )}

          <div className="new-order-modal-field" style={{ position: 'relative' }}>
            <label className="new-order-modal-label">
              {formData.accountType === "Merchant Account" ? "Search Merchant" : "Search PDL"}
            </label>
            <div style={{ display: 'flex', gap: '8px' }}>
              <div style={{ position: 'relative', flex: 1 }}>
                <Search
                  size={18}
                  style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }}
                />
                <input
                  type="text"
                  className="new-order-modal-input"
                  value={formData.pdlKeyword}
                  onChange={(e) => handleChange('pdlKeyword', e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleSearchPdl())}
                  placeholder={formData.accountType === "Merchant Account" ? "Search Merchant..." : "Search PDL..."}
                  style={{ paddingLeft: '40px' }}
                />
              </div>
              <button
                type="button"
                className="new-order-modal-proceed-btn"
                style={{ padding: '8px 16px', minWidth: 'auto' }}
                onClick={handleSearchPdl}
                disabled={pdlSearching}
              >
                {pdlSearching ? "Searching..." : "Search"}
              </button>
            </div>
            {showPdlDropdown && pdlSearchResults.length > 0 && (
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  marginTop: '4px',
                  backgroundColor: '#fff',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  maxHeight: '200px',
                  overflowY: 'auto',
                  zIndex: 10,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                }}
              >
                {pdlSearchResults.map((pdl, idx) => {
                  const isMerchantAccount = formData.accountType === "Merchant Account";
                  const displayText = isMerchantAccount 
                    ? `${pdl.merchantName ?? '—'} - ${pdl.location ?? '—'}` 
                    : `${pdl.pdlName ?? pdl.name ?? pdl.fullName ?? '—'}${(pdl.pdlNumber ?? pdl.pdl_number) ? ` (${pdl.pdlNumber ?? pdl.pdl_number})` : ''}`;
                  
                  return (
                    <div
                      key={pdl.merchantId ?? pdl.id ?? idx}
                      onClick={() => handleSelectPdl(pdl)}
                      style={{
                        padding: '10px 12px',
                        cursor: 'pointer',
                        borderBottom: idx < pdlSearchResults.length - 1 ? '1px solid #f3f4f6' : 'none'
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f9fafb'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                    >
                      {displayText}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="new-order-modal-row">
            <div className="new-order-modal-field new-order-modal-field-half">
              <label className="new-order-modal-label">Name</label>
              <input
                type="text"
                className="new-order-modal-input"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="Name"
              />
            </div>
            <div className="new-order-modal-field new-order-modal-field-half">
              <label className="new-order-modal-label">Location</label>
              <input
                type="text"
                className="new-order-modal-input"
                value={formData.location}
                onChange={(e) => handleChange('location', e.target.value)}
                placeholder="Location"
              />
            </div>
          </div>

          {isSpecialOrder && (
            <div className="new-order-modal-field">
              <label className="new-order-modal-label">Reason for Purchase</label>
              <textarea
                className="new-order-modal-input"
                value={formData.reasonForPurchase}
                onChange={(e) => handleChange('reasonForPurchase', e.target.value)}
                placeholder="Reason for purchase..."
                rows={3}
                style={{ resize: 'vertical' }}
              />
            </div>
          )}
        </div>

        <div className="new-order-modal-actions">
          <button className="new-order-modal-cancel-btn" onClick={handleClose}>Cancel</button>
          <button className="new-order-modal-proceed-btn" onClick={handleProceed}>Proceed</button>
        </div>
      </div>
    </Modal>
  );
};

export default NewOrderModal;
